/** Iter 639 — staff till split · Earnings tab (view_earnings_ledger only).
 * Takes the till bag `{ t }` like every other till card. */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Wallet } from "lucide-react";
import { tillApi, detail, money } from "@/features/staffTill/helpers";

// eslint-disable-next-line no-unused-vars
export default function EarningsCard({ t }) {
  const q = useQuery({
    queryKey: ["till-earnings"],
    retry: 0,
    queryFn: async () => (await tillApi.get("/classifieds/engagement/till/earnings")).data,
  });
  if (q.isLoading) {
    return <div className="mt-8 grid place-items-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (q.isError) {
    return <div className="mt-4 rounded-2xl bg-white shadow-card p-6 text-sm text-slate-500" data-testid="till-earnings-error">{detail(q.error, "Couldn't load earnings.")}</div>;
  }
  // Exact backend shape: {transactions:[], total_collected, count_paid,
  // deferred_payables_outstanding} (verified live).
  const rows = q.data?.transactions || [];
  const totalCollected = money(q.data?.total_collected);
  const countPaid = q.data?.count_paid;
  const deferred = Number(q.data?.deferred_payables_outstanding) || 0;
  return (
    <div className="mt-4 rounded-2xl bg-white shadow-card p-5" data-testid="till-earnings">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
        <Wallet className="w-4 h-4" /> Earnings
      </div>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600" data-testid="till-earnings-totals">
        <span>Collected <span className="font-bold text-slate-900">{totalCollected ?? "$0.00"}</span></span>
        <span>Paid sessions <span className="font-bold text-slate-900">{countPaid ?? 0}</span></span>
        {deferred > 0 && (
          <span>Outstanding <span className="font-bold text-amber-700">{money(deferred)}</span></span>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No transactions yet.</p>
      ) : (
        <ul className="mt-3 space-y-1.5 text-sm text-slate-600 max-h-80 overflow-y-auto">
          {rows.map((r, i) => {
            // WO 801 §3 — payout statement fields (set once Stripe confirms
            // the real fee): one "Card cost adjustment" line when ≠ 0 and
            // "Net to store" = vendor_take_final when present.
            const adj = Number(r.card_cost_adjustment_nzd) || 0;
            const net = r.vendor_take_final ?? r.vendor_take_after_recovery ?? r.vendor_take;
            const adjReason =
              r.card_cost_adjustment_reason === "international_card" ? "international card" : "rounding";
            return (
              <li key={r.id || i} className="rounded-lg bg-slate-50 px-3 py-2 flex flex-wrap gap-x-4">
                {r.short_code && <span className="font-bold tracking-widest">{r.short_code}</span>}
                {r.description && <span>{r.description}</span>}
                {(r.amount ?? r.charge_total) != null && <span className="ml-auto font-semibold text-slate-900">{money(r.amount ?? r.charge_total)}</span>}
                {r.service_fee_total != null && (
                  <span className="w-full text-xs text-slate-500" data-testid="till-earn-service-fee">
                    Service fee {money(r.service_fee_total)}
                  </span>
                )}
                {adj !== 0 && (
                  <span className="w-full text-xs text-amber-700" data-testid="till-earn-card-adjustment">
                    Card cost adjustment ({adjReason}) {adj < 0 ? `−${money(Math.abs(adj))}` : `+${money(adj)}`}
                  </span>
                )}
                {net != null && (
                  <span className="w-full text-xs font-bold text-slate-700" data-testid="till-earn-net">
                    Net to store {money(net)}
                  </span>
                )}
                {(r.created_at || r.at) && <span className="text-slate-400 text-xs w-full">{new Date(r.created_at || r.at).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
